import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Download, Search, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { sr } from "../../lib/sr";

export default function AdminSmsOptIns() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [denied, setDenied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = () => sr.adminSmsOptIns({ status, q: q || undefined })
    .then((d) => { setData(d); setDenied(false); })
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); setData({ optins: [], total: 0, confirmed: 0, opted_out: 0 }); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { sr.smsOptInConfig().then(setCfg).catch(() => {}); }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const blob = await sr.adminSmsOptInsExport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sms-optins.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Opt-ins exported");
    } catch { toast.error("Could not export"); }
    finally { setExporting(false); }
  };

  const doCompliancePdf = async () => {
    setPdfing(true);
    try {
      const blob = await sr.adminSmsOptInsCompliancePdf();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "sms-optin-compliance.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Compliance snapshot exported");
    } catch { toast.error("Could not export PDF"); }
    finally { setPdfing(false); }
  };

  if (denied) return <div style={{ padding: 24 }} className="sr-muted" data-testid="sms-optins-denied">Staff access only.</div>;

  const rows = data?.optins || [];
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } };

  return (
    <div style={{ padding: "8px 4px" }} data-testid="admin-sms-optins">
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 4 }}>
        <MessageSquareText size={18} style={{ color: "var(--gold)" }} />
        <h1 style={{ margin: 0, fontSize: 20, color: "var(--navy)" }}>SMS Opt-ins</h1>
      </div>
      <p className="sr-muted" style={{ marginTop: 0, fontSize: 13.5 }}>
        Consent records collected at <strong>/sms-opt-in</strong> — hand the CSV to your registrar as proof of opt-in.
      </p>

      {cfg?.legal_entity && (
        <div data-testid="optins-brand-banner" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#EEF4FF", border: "1px solid #C7D7FE", color: "#3538CD", padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
          <ShieldCheck size={14} /> Registered brand: {cfg.legal_entity}
          {cfg.brand ? <span style={{ opacity: 0.75, fontWeight: 500 }}>· trade name {cfg.brand}</span> : null}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        {["ALL", "confirmed", "opted_out"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`optins-filter-${s}`}
            className={`sr-btn sr-btn-sm ${status === s ? "sr-btn-primary" : "sr-btn-ghost"}`}>
            {s === "ALL" ? "All" : s === "confirmed" ? "Confirmed" : "Opted out"}
            {s === "confirmed" && data ? ` (${data.confirmed})` : ""}
            {s === "opted_out" && data ? ` (${data.opted_out})` : ""}
          </button>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 9, top: 9, color: "var(--muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Phone or name"
              data-testid="optins-search" style={{ padding: "7px 10px 7px 28px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 }} />
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={doExport} disabled={exporting || !rows.length} data-testid="optins-export">
            {exporting ? <Loader2 size={13} className="sr-spin" /> : <><Download size={13} /> CSV</>}
          </button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={doCompliancePdf} disabled={pdfing} data-testid="optins-compliance-pdf">
            {pdfing ? <Loader2 size={13} className="sr-spin" /> : <><ShieldCheck size={13} /> Compliance PDF</>}
          </button>
        </div>
      </div>

      {!data ? (
        <div style={{ padding: 24 }}><Loader2 className="sr-spin" size={18} /></div>
      ) : rows.length === 0 ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 30 }} data-testid="optins-empty">
          <p className="sr-muted" style={{ margin: 0 }}>No opt-ins yet. Share <strong>/sms-opt-in</strong> to start collecting consent.</p>
        </div>
      ) : (
        <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="optins-table">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: "var(--surface)", textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "10px 14px", fontWeight: 600 }}>Phone</th>
                <th style={{ padding: "10px 14px", fontWeight: 600 }}>Name</th>
                <th style={{ padding: "10px 14px", fontWeight: 600 }}>Status</th>
                <th style={{ padding: "10px 14px", fontWeight: 600 }}>Source</th>
                <th style={{ padding: "10px 14px", fontWeight: 600 }}>Opted in</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`optin-row-${r.id}`}>
                  <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--navy)" }}>{r.phone}</td>
                  <td style={{ padding: "10px 14px" }}>{r.name || "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {r.status === "opted_out" ? (
                      <span className="sr-badge" style={{ background: "#FEF3F2", color: "#B42318", display: "inline-flex", alignItems: "center", gap: 4 }}><XCircle size={12} /> Opted out</span>
                    ) : (
                      <span className="sr-badge" style={{ background: "#E7F7EF", color: "#12805C", display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle2 size={12} /> Confirmed</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }} className="sr-muted">{r.source || "—"}</td>
                  <td style={{ padding: "10px 14px" }} className="sr-muted">{fmt(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
